import {
  type DocumentTagEntry,
  type DocumentTagMenuCategory,
  type DocumentTagMenuTag,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { DEFAULT_CATEGORY_COLOR } from "@/constants/KnowledgeBaseConstant"
import type {
  DocumentTagsResponse,
  KnowledgeBaseCategory,
} from "@/types/knowledgebaseCategories"
import { getLocalizedLabel } from "./TagDocumentShared"

export type SelectedTagIdsByCategory = Record<string, string[]>

export const getPersistedSelectedTagIdsByCategory = (
  allTagEntries: DocumentTagEntry[],
): SelectedTagIdsByCategory => {
  return allTagEntries.reduce<SelectedTagIdsByCategory>(
    (accumulator, entry) => {
      const existingTagIds = accumulator[entry.category.categoryId] ?? []
      accumulator[entry.category.categoryId] = [
        ...existingTagIds,
        entry.tag.tagId,
      ]
      return accumulator
    },
    {},
  )
}

export const getPreservedHiddenTagIds = (
  allTagEntries: DocumentTagEntry[],
  categories: KnowledgeBaseCategory[],
) => {
  const selectableCategoryIdSet = new Set(
    categories.map((category) => category.id),
  )

  return Array.from(
    new Set(
      allTagEntries
        .filter(
          (entry) => !selectableCategoryIdSet.has(entry.category.categoryId),
        )
        .map((entry) => entry.tag.tagId),
    ),
  )
}

export const sanitizeDraftSelectedTagIdsByCategory = ({
  currentSelections,
  categories,
  persistedSelectedTagIdsByCategory,
}: {
  currentSelections: SelectedTagIdsByCategory
  categories: KnowledgeBaseCategory[]
  persistedSelectedTagIdsByCategory: SelectedTagIdsByCategory
}): SelectedTagIdsByCategory => {
  let hasChanges = false
  const validTagIdsByCategory = new Map(
    categories.map((category) => [
      category.id,
      new Set(category.tags.filter((tag) => tag.isActive).map((tag) => tag.id)),
    ]),
  )
  const nextSelections: SelectedTagIdsByCategory = {}

  for (const [categoryId, selectedTagIds] of Object.entries(
    currentSelections,
  )) {
    const persistedForCategory = new Set(
      persistedSelectedTagIdsByCategory[categoryId] ?? [],
    )
    const validTagIds = validTagIdsByCategory.get(categoryId)

    if (!validTagIds) {
      const kept = selectedTagIds.filter((id) => persistedForCategory.has(id))
      if (kept.length !== selectedTagIds.length) hasChanges = true
      if (kept.length > 0) nextSelections[categoryId] = kept
      continue
    }

    const filteredTagIds = selectedTagIds.filter(
      (tagId) => validTagIds.has(tagId) || persistedForCategory.has(tagId),
    )
    if (filteredTagIds.length !== selectedTagIds.length) hasChanges = true
    nextSelections[categoryId] = filteredTagIds
  }

  return hasChanges ? nextSelections : currentSelections
}

export const mergeNewPersistedSelectedTagIdsByCategory = ({
  currentSelections,
  previousPersistedSelectedTagIdsByCategory,
  persistedSelectedTagIdsByCategory,
}: {
  currentSelections: SelectedTagIdsByCategory
  previousPersistedSelectedTagIdsByCategory: SelectedTagIdsByCategory
  persistedSelectedTagIdsByCategory: SelectedTagIdsByCategory
}): SelectedTagIdsByCategory => {
  let hasChanges = false
  const nextSelections: SelectedTagIdsByCategory = { ...currentSelections }

  for (const [categoryId, persistedTagIds] of Object.entries(
    persistedSelectedTagIdsByCategory,
  )) {
    const previousPersistedTagIdSet = new Set(
      previousPersistedSelectedTagIdsByCategory[categoryId] ?? [],
    )
    const newlyPersistedTagIds = persistedTagIds.filter(
      (tagId) => !previousPersistedTagIdSet.has(tagId),
    )

    if (newlyPersistedTagIds.length === 0) continue

    const currentTagIds = nextSelections[categoryId] ?? []
    const currentTagIdSet = new Set(currentTagIds)
    const mergedTagIds = [
      ...currentTagIds,
      ...newlyPersistedTagIds.filter((tagId) => !currentTagIdSet.has(tagId)),
    ]

    if (mergedTagIds.length !== currentTagIds.length) {
      nextSelections[categoryId] = mergedTagIds
      hasChanges = true
    }
  }

  return hasChanges ? nextSelections : currentSelections
}

export const createDraftSelectedTags = ({
  categories,
  draftSelectedTagIdsByCategory,
  allTagEntries,
  currentLanguage,
}: {
  categories: KnowledgeBaseCategory[]
  draftSelectedTagIdsByCategory: SelectedTagIdsByCategory
  allTagEntries: DocumentTagEntry[]
  currentLanguage: string
}): DocumentTagEntry[] => {
  const knownEntriesByTagId = new Map(
    allTagEntries.map((entry) => [entry.tag.tagId, entry]),
  )

  return categories.flatMap((category) => {
    const selectedTagIds = draftSelectedTagIdsByCategory[category.id] ?? []
    const menuCategory: DocumentTagMenuCategory = {
      categoryId: category.id,
      categoryDisplayNameEn: getLocalizedLabel(
        category.displayNameEn,
        category.displayNameDe,
        currentLanguage,
      ),
      categoryColor: category.color || DEFAULT_CATEGORY_COLOR,
      tags: [],
    }

    return selectedTagIds.flatMap((id): DocumentTagEntry[] => {
      const existing = knownEntriesByTagId.get(id)
      if (existing) return [{ ...existing, category: menuCategory }]

      const kbTag = category.tags.find((tag) => tag.id === id)
      if (!kbTag) return []

      const newTag: DocumentTagMenuTag = {
        id: kbTag.id,
        tagId: kbTag.id,
        tag: kbTag,
        source: "user",
        approvedAt: null,
        categoryColor: category.color || DEFAULT_CATEGORY_COLOR,
      }

      return [{ category: menuCategory, tag: newTag }]
    })
  })
}

export const groupSelectedTagsByCategory = (
  draftSelectedTags: DocumentTagEntry[],
) => {
  return draftSelectedTags.reduce<Record<string, DocumentTagEntry[]>>(
    (accumulator, entry) => {
      const existing = accumulator[entry.category.categoryId] ?? []
      accumulator[entry.category.categoryId] = [...existing, entry]
      return accumulator
    },
    {},
  )
}

export const hasPendingTagSelectionChanges = ({
  categories,
  draftSelectedTagIdsByCategory,
  persistedSelectedTagIdsByCategory,
}: {
  categories: KnowledgeBaseCategory[]
  draftSelectedTagIdsByCategory: SelectedTagIdsByCategory
  persistedSelectedTagIdsByCategory: SelectedTagIdsByCategory
}) => {
  for (const categoryId of categories.map((category) => category.id)) {
    const persistedTagIds = [
      ...(persistedSelectedTagIdsByCategory[categoryId] ?? []),
    ].sort()
    const draftTagIds = [
      ...(draftSelectedTagIdsByCategory[categoryId] ?? []),
    ].sort()

    if (persistedTagIds.length !== draftTagIds.length) return true
    if (persistedTagIds.some((tagId, index) => tagId !== draftTagIds[index])) {
      return true
    }
  }

  return false
}

export const buildDocumentTagSavePayload = ({
  documentTags,
  draftSelectedTagIdsByCategory,
  preservedHiddenTagIds,
}: {
  documentTags: DocumentTagsResponse | undefined
  draftSelectedTagIdsByCategory: SelectedTagIdsByCategory
  preservedHiddenTagIds: string[]
}) => {
  const approvedAutoEntries = (documentTags?.categories ?? []).flatMap(
    (category) =>
      category.tags.filter(
        (tag) => tag.source === "auto" && tag.approvedAt !== null,
      ),
  )
  const approvedAutoIdSet = new Set(approvedAutoEntries.map((tag) => tag.tagId))
  const draftTagIdSet = new Set(
    Object.values(draftSelectedTagIdsByCategory).flat(),
  )
  const removedApprovedAutoTagIds = approvedAutoEntries
    .filter((tag) => !draftTagIdSet.has(tag.tagId))
    .map((tag) => tag.tagId)

  const nextTagIds = Array.from(
    new Set([
      ...Object.values(draftSelectedTagIdsByCategory)
        .flat()
        .filter((id) => !approvedAutoIdSet.has(id)),
      ...preservedHiddenTagIds,
    ]),
  )

  return {
    nextTagIds,
    removedApprovedAutoTagIds,
  }
}
